import { countries } from 'country-data'
import React, { useState } from 'react'
import { Alert, Button } from 'react-bootstrap'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { bindActionCreators } from 'redux'
import us from 'us'
import validator from 'validator'
import { fetchBusinessById } from '../../actions/business'
import {
  cancelBusinessAddressModalDialog,
  closeBusinessAddressModalDialog,
  createBusinessAddress,
  saveChangesBusinessAddressModalDialog,
  updateBusinessAddress,
  validateBusinessAddressModalDialog,
} from '../../actions/business_address_modal_dialog'
import { updateBusinessInformation } from '../../actions/business_info_modal_dialog'
import { FORM_VALIDATION_ERROR, FORM_VALIDATION_SUCCESS } from '../../config'
import SelectFieldFormGroup from '../common/form/select_field_form_group'
import TextFieldFormGroup from '../common/form/text_field_form_group'
import Spinner3Bounce from '../spinner_3_bounce'

const addressTypeOptions = [
  {
    label: 'Physical',
    value: 'physical',
  },
  {
    label: 'Registered',
    value: 'registered',
  },
]

const countryOptions = countries.all.map((i) => ({
  value: i.alpha2,
  label: `${i.alpha2} ${i.name}`,
}))

const usStates = Object.entries(us.states).map(([key, entity]) => ({ label: entity.name, value: entity.abbr }))
const validate = (value, required = false, validators = []) => {
  if (required) {
    if (value.length < 1) {
      return 'error'
    }
  }

  if (value.length < 1) {
    return
  }

  for (let val in validators) {
    let result = true
    switch (validators[val]) {
      case 'isEmail':
        result = validator.isEmail(value)
        break
      case 'isLength':
        result = validator.isLength(value, { min: 3 })
        break
      default:
        throw Error('Invalid validator type')
    }
    if (value.length > 0 && !result) {
      return FORM_VALIDATION_ERROR
    }
  }
  return FORM_VALIDATION_SUCCESS
}
const BusinessAddressForm = ({
  data,
  isChangesSaved,
  isValidated,
  cancelBusinessAddressModalDialog,
  updateBusinessAddress,
  createBusinessAddress,
  saveChangesBusinessAddressModalDialog,
  closeBusinessAddressModalDialog,
  fetchBusinessById,
  isSaving,
  business,
  ...rest
}) => {
  const [address, setAddress] = useState(data.address || '')
  const [addressValidationState, setAddressValidationState] = useState(null)
  const onAddressChange = (e) => {
    setAddress(e.target.value)
    setAddressValidationState(validate(e.target.value, true))
  }

  const [city, setCity] = useState(data.city || '')
  const [cityValidationState, setCityValidationState] = useState(null)
  const onCityChange = (e) => {
    setCity(e.target.value)
    setCityValidationState(validate(e.target.value, true))
  }

  const [state, setState] = useState(data.state || '')
  const [stateValidationState, setStateValidationState] = useState(null)
  const onStateChange = (e) => {
    setState(e.target.value)
    setStateValidationState(validate(e.target.value, true))
  }

  const [postalCode, setPostalCode] = useState(data.postalcode || '')
  const [postalCodeValidationState, setPostalCodeValidationState] = useState(null)
  const onPostalCodeChange = (e) => {
    setPostalCode(e.target.value)
    setPostalCodeValidationState(validate(e.target.value, true))
  }

  const [unit, setUnit] = useState(data.unit || '')
  const onUnitChange = (e) => setUnit(e.target.value)

  const [addressType, setAddressType] = useState(data.address_type || '')
  const [addressTypeValidationState, setAddressTypeValidationState] = useState(null)
  const onAddressTypeChange = (e) => {
    setAddressType(e.target.value)
    setAddressTypeValidationState(validate(e.target.value, true))
  }

  const [countryCode, setCountryCode] = useState(data.country_code || '')
  const [countryCodeValidationState, setCountryCodeValidationState] = useState(null)
  const onCountryCodeChange = (e) => {
    setCountryCode(e.target.value)
    setCountryCodeValidationState(validate(e.target.value, true))
  }
  const handleCloseModal = (e) => {
    fetchBusinessById(data.business)
    closeBusinessAddressModalDialog()
  }
  const isValid = () => {
    const states = [
      validate(address, true),
      validate(city, true),
      validate(state, true),
      validate(postalCode, true),
      validate(addressType, true),
      validate(countryCode, true),
    ]
    return states.every((i) => i === 'success')
  }

  const handleSaveChanges = (e) => {
    const entity = {
      address_type: addressType,
      country_code: countryCode,
      address,
      city,
      state,
      score: 0,
      unit,
      postalcode: postalCode,
    }
    if (data.id) {
      updateBusinessAddress(data.business, data.id, entity)
    } else {
      createBusinessAddress(data.business, entity)
    }

    saveChangesBusinessAddressModalDialog(entity)
  }

  return (
    <form>
      {isSaving && (
        <div className='text-center grid-spinner-mt-60'>
          <Spinner3Bounce isActive />
        </div>
      )}
      {!isChangesSaved && !isSaving && (
        <div className='fadeOutDown'>
          <SelectFieldFormGroup
            help='Required'
            value={addressType}
            controlId='addressType'
            controlLabel='Address type'
            data={addressTypeOptions}
            propName={'value'}
            labelName={'label'}
            validationState={addressTypeValidationState}
            handleFieldChange={onAddressTypeChange}
          />

          <TextFieldFormGroup
            help='Required'
            value={address}
            controlId='address'
            controlLabel='Address'
            validationState={addressValidationState}
            handleFieldChange={onAddressChange}
          />

          <TextFieldFormGroup value={unit} controlId='unit' controlLabel='Address 2' handleFieldChange={onUnitChange} />

          <TextFieldFormGroup
            help='Required'
            value={city}
            controlId='city'
            controlLabel='City'
            validationState={cityValidationState}
            handleFieldChange={onCityChange}
          />

          {countryCode === 'US' && (
            <SelectFieldFormGroup
              help='Required'
              value={state}
              controlId='state'
              controlLabel='State'
              propName={'value'}
              labelName={'label'}
              data={usStates}
              validationState={stateValidationState}
              handleFieldChange={onStateChange}
            />
          )}
          {countryCode !== 'US' && (
            <TextFieldFormGroup
              help='Required'
              value={state}
              controlId='state'
              controlLabel='State'
              validationState={stateValidationState}
              handleFieldChange={onStateChange}
            />
          )}

          <TextFieldFormGroup
            help='Required'
            value={postalCode}
            controlId='postalCode'
            controlLabel='Postal code'
            validationState={postalCodeValidationState}
            handleFieldChange={onPostalCodeChange}
          />

          <SelectFieldFormGroup
            help='Required'
            value={countryCode}
            controlId='countryCode'
            controlLabel='Country Code'
            data={countryOptions}
            labelName={'label'}
            propName={'value'}
            validationState={countryCodeValidationState}
            handleFieldChange={onCountryCodeChange}
          />
        </div>
      )}
      {isChangesSaved && !isSaving && (
        <Alert className='m-b-sm m-t-sm' variant='success'>
          Information has been successfully updated
        </Alert>
      )}
      <div className='text-end'>
        {isChangesSaved ? (
          <Button className='m-r-sm' onClick={(e) => handleCloseModal(e)}>
            Close
          </Button>
        ) : (
          <Button className='m-r-sm' onClick={() => cancelBusinessAddressModalDialog()}>
            Cancel
          </Button>
        )}
        {!isChangesSaved && isValidated && (
          <Button variant='primary' disabled={!isValid()} onClick={(e) => handleSaveChanges(e)}>
            Save changes
          </Button>
        )}
        {!isChangesSaved && !isValidated && (
          <Button disabled variant='primary' onClick={(e) => handleSaveChanges(e)}>
            Save changes
          </Button>
        )}
      </div>
    </form>
  )
}
const mapStateToProps = (state) => ({
  isChangesSaved: state.businessAddressModalDialog.isChangesSaved,
  isValidated: state.businessAddressModalDialog.isValidated,
  isSaving: state.businessAddressModalDialog.isSaving,
})

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      validateBusinessAddressModalDialog,
      closeBusinessAddressModalDialog,
      cancelBusinessAddressModalDialog,
      saveChangesBusinessAddressModalDialog,
      updateBusinessInformation,
      updateBusinessAddress,
      createBusinessAddress,
      fetchBusinessById,
    },
    dispatch
  )

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(BusinessAddressForm))
